import { z } from "zod";
import { AllergyCodeSystemSchema, ChartCodeSystemSchema, MedicationCodeSystemSchema, ProblemCategorySchema, ProblemClinicalStatusSchema, } from "./chart-history.js";
import { AllergySeveritySchema, AllergyStatusSchema, FamilyRelationSchema, HistoryRowStatusSchema, ImmunizationCodeSystemSchema, ImmunizationRouteSchema, ImmunizationStatusSchema, MedicationStatusSchema, SocialHistoryKindSchema, SocialHistoryStatusSchema, } from "./patient-profile.js";
import { RegisterPatientInputSchema } from "./patient-registration.js";
import { IdempotencyKeySchema, IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
const IsoDateSchema = z.string().date();
/**
 * An imported allergy or medication may carry a code without a system, as the
 * previous EMR recorded it; a system without a code is refused (PC-4).
 */
function systemNeedsCode(value, context) {
    if (value.codeSystem !== undefined && value.code === undefined) {
        context.addIssue({ code: "custom", path: ["code"], message: "A code system needs a code" });
    }
}
/** Both halves of a code must arrive together, as the chart writers require. */
function pairedCode(codeKey, systemKey) {
    return (value, context) => {
        if ((value[codeKey] === undefined) !== (value[systemKey] === undefined)) {
            context.addIssue({
                code: "custom",
                path: [value[codeKey] === undefined ? codeKey : systemKey],
                message: `${codeKey} and ${systemKey} must be provided together`,
            });
        }
    };
}
/**
 * Importing a chart from a previous EMR.
 *
 * Two rules shape every schema here. Nothing in the run ledger may carry
 * patient content: options, summaries and item details hold flags, counts and
 * exception codes, and the importer's own local report holds the rest. And
 * every chart row records where it came from — the run, the source reference,
 * and the clinician who wrote or reviewed it at the previous clinic — so a
 * reader can always tell imported history from what this clinic asserted.
 *
 * Mirrors api.import_run and api.import_patient.
 */
export const ImportSourceSystemSchema = z
    .string()
    .regex(/^[a-z0-9][a-z0-9:._-]{1,79}$/, "a source system is a slug, such as omd-cds:telus-ps");
export const ImportRunStatusSchema = z.enum(["running", "completed", "failed", "cancelled"]);
export const ImportItemStatusSchema = z.enum([
    "created",
    "linked",
    "unchanged",
    "skipped",
    "conflict",
    "failed",
]);
export const ImportItemKindSchema = z.enum([
    "patient",
    "identifier",
    "allergy",
    "medication",
    "condition",
    "family_history",
    "social_history",
    "immunization",
    "lab_observation",
    "lab_summary",
    "legacy_visit",
    "administrative_notes",
    "document",
    "status",
]);
export const ImportRunOptionsSchema = z
    .object({
    /** file_only files bytes now and leaves extraction to a later reprocess. */
    documentsProcessing: z.enum(["extract", "file_only"]).optional(),
    /** reject stops a matching chart for a human; nothing merges silently. */
    duplicatePolicy: z.enum(["reject", "link", "create"]).optional(),
    warmContext: z.boolean().optional(),
})
    .strict();
export const StartImportRunSchema = z
    .object({
    sourceSystem: ImportSourceSystemSchema,
    sourceLabel: z.string().trim().min(1).max(160),
    options: ImportRunOptionsSchema.optional(),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const ImportRunSchema = z
    .object({
    runId: IdSchema,
    siteId: IdSchema,
    sourceSystem: ImportSourceSystemSchema,
    sourceLabel: z.string().min(1).max(160),
    status: ImportRunStatusSchema,
    options: ImportRunOptionsSchema,
    /** Counts only. Never a patient name, a number or a filename. */
    summary: z.record(z.string(), z.unknown()),
    version: z.number().int().positive(),
    startedAt: IsoDateTimeSchema,
    finishedAt: IsoDateTimeSchema.optional(),
})
    .passthrough();
/** The only fields an exception may carry, so a report can never leak a chart. */
export const ImportItemDetailSchema = z
    .object({
    code: z.string().max(80).optional(),
    message: z.string().max(500).optional(),
    matchedOn: z.array(z.string().max(40)).optional(),
})
    .strict();
export const ImportItemSchema = z
    .object({
    sourcePatientKey: z.string().min(1).max(160),
    itemKind: ImportItemKindSchema,
    itemKey: z.string().min(1).max(240),
    status: ImportItemStatusSchema,
    contentHash: Sha256Schema,
    targetTable: z.string().max(120).optional(),
    targetId: IdSchema.optional(),
    patientId: IdSchema.optional(),
    detail: ImportItemDetailSchema.optional(),
    updatedAt: IsoDateTimeSchema.optional(),
})
    .passthrough();
export const RecordImportItemSchema = z
    .object({
    runId: IdSchema,
    sourcePatientKey: z.string().min(1).max(160),
    itemKind: ImportItemKindSchema,
    itemKey: z.string().min(1).max(240),
    contentHash: Sha256Schema,
    status: ImportItemStatusSchema,
    targetTable: z.string().max(120).optional(),
    targetId: IdSchema.optional(),
    patientId: IdSchema.optional(),
    detail: ImportItemDetailSchema.optional(),
})
    .strict();
export const FinishImportRunSchema = z
    .object({
    runId: IdSchema,
    expectedVersion: z.number().int().positive(),
    status: z.enum(["completed", "failed", "cancelled"]),
    summary: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Chart sections
// ---------------------------------------------------------------------------
/** Who wrote or reviewed this entry at the previous clinic, and when. */
export const ImportOriginalProvenanceSchema = z
    .object({
    author: z.string().max(200).optional(),
    recordedAt: z.string().max(40).optional(),
    reviewer: z.string().max(200).optional(),
    reviewedAt: z.string().max(40).optional(),
})
    .strict();
/**
 * An imported chart entry is the clinic's own write shape minus the three keys
 * that describe an edit made here (`id`, `version`, `sourceKind`), plus where
 * it came from.
 *
 * The fields are declared here rather than imported from the profile contract,
 * which is byte-pinned to an approved model release and must not move for a
 * schema change. `patient-import.test.ts` compares these key sets against the
 * profile write schemas, so drift fails a test instead of passing silently.
 */
const importedBase = {
    sourceReference: z.string().min(1).max(240),
    original: ImportOriginalProvenanceSchema.optional(),
};
export const ImportAllergySchema = z
    .object({
    ...importedBase,
    display: z.string().min(1).max(240),
    code: z.string().min(1).max(80).optional(),
    codeSystem: AllergyCodeSystemSchema.optional(),
    reaction: z.string().min(1).max(500).optional(),
    severity: AllergySeveritySchema.optional(),
    status: AllergyStatusSchema.optional(),
})
    .strict()
    .superRefine(systemNeedsCode);
export const ImportMedicationSchema = z
    .object({
    ...importedBase,
    display: z.string().min(1).max(240),
    code: z.string().min(1).max(80).optional(),
    codeSystem: MedicationCodeSystemSchema.optional(),
    sig: z.string().min(1).max(500).optional(),
    status: MedicationStatusSchema.optional(),
})
    .strict()
    .superRefine(systemNeedsCode);
export const ImportPmhSchema = z
    .object({
    ...importedBase,
    display: z.string().min(1).max(240),
    code: z.string().min(1).max(80).optional(),
    codeSystem: ChartCodeSystemSchema.optional(),
    onsetDate: IsoDateSchema.optional(),
    note: z.string().min(1).max(2000).optional(),
    category: ProblemCategorySchema.optional(),
    status: ProblemClinicalStatusSchema.optional(),
})
    .strict()
    .superRefine(pairedCode("code", "codeSystem"));
export const ImportFamilyHistoryRowSchema = z
    .object({
    ...importedBase,
    relation: FamilyRelationSchema,
    display: z.string().min(1).max(240),
    code: z.string().min(1).max(80).optional(),
    codeSystem: ChartCodeSystemSchema.optional(),
    onsetAge: z.coerce.number().int().min(0).max(130).optional(),
    deceased: z.boolean().optional(),
    note: z.string().min(1).max(2000).optional(),
    status: HistoryRowStatusSchema.optional(),
})
    .strict()
    .superRefine(pairedCode("code", "codeSystem"));
export const ImportSocialHistorySchema = z
    .object({
    ...importedBase,
    kind: SocialHistoryKindSchema,
    status: SocialHistoryStatusSchema,
    detail: z.string().min(1).max(500),
    quantity: z.string().min(1).max(120).optional(),
    frequency: z.string().min(1).max(120).optional(),
    startedOn: IsoDateSchema.optional(),
    endedOn: IsoDateSchema.optional(),
    note: z.string().min(1).max(2000).optional(),
    clinicalStatus: HistoryRowStatusSchema.optional(),
})
    .strict();
export const ImportImmunizationSchema = z
    .object({
    ...importedBase,
    display: z.string().min(1).max(240),
    code: z.string().min(1).max(20).optional(),
    codeSystem: ImmunizationCodeSystemSchema.optional(),
    administeredOn: IsoDateSchema,
    lotNumber: z.string().min(1).max(80).optional(),
    site: z.string().min(1).max(80).optional(),
    route: ImmunizationRouteSchema.optional(),
    doseNumber: z.coerce.number().int().min(1).max(20).optional(),
    note: z.string().min(1).max(2000).optional(),
    status: ImmunizationStatusSchema.optional(),
})
    .strict()
    .superRefine(pairedCode("code", "codeSystem"));
export const ImportPatientExternalIdentifierSchema = z
    .object({
    system: z.string().regex(/^[a-z0-9][a-z0-9:._-]{1,79}$/, "an identifier system is a slug"),
    value: z.string().min(1).max(120),
    label: z.string().max(120).optional(),
    attributes: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
export const ImportLabResultSchema = z
    .object({
    sourceReference: z.string().min(1).max(240),
    code: z.string().max(120).optional(),
    codeSystem: z.enum(["LOINC", "local"]).optional(),
    display: z.string().min(1).max(240),
    /** Sent as strings so a decimal never loses a digit in transit. */
    valueNumeric: z.string().max(40).optional(),
    valueText: z.string().max(4000).optional(),
    unit: z.string().max(80).optional(),
    referenceLow: z.string().max(40).optional(),
    referenceHigh: z.string().max(40).optional(),
    referenceText: z.string().max(500).optional(),
    flag: z.string().max(16).optional(),
    interpretation: z.enum(["normal", "abnormal", "critical", "unknown"]).optional(),
    resultStatus: z.enum(["preliminary", "final", "corrected", "unknown"]).optional(),
    collectedAt: IsoDateTimeSchema.optional(),
    note: z.string().max(2000).optional(),
})
    .strict();
export const ImportLabAccessionSchema = z
    .object({
    accessionNumber: z.string().min(1).max(120),
    collectedAt: IsoDateTimeSchema,
    reportedAt: IsoDateTimeSchema.optional(),
    panelName: z.string().max(240).optional(),
    /**
     * The laboratory's own code for the panel (LifeLabs writes `HAEM1` on every
     * analyte of a CBC). It names the requisition, never an analyte, which is
     * why the analytes below carry no code of their own from this source.
     */
    panelCode: z.string().max(40).optional(),
    /** One readable line per value; this is what the AI context reads. */
    summary: z.string().min(1).max(2000),
    interpretation: z.enum(["normal", "abnormal", "critical", "unknown"]).optional(),
    reviewedBy: z.string().max(160).optional(),
    reviewedAt: IsoDateTimeSchema.optional(),
    results: z.array(ImportLabResultSchema).max(200),
})
    .strict();
export const ImportLegacyVisitSchema = z
    .object({
    sourceReference: z.string().min(1).max(240),
    kind: z.enum(["appointment", "note"]),
    linkedSourceReference: z.string().max(240).optional(),
    /** Must carry an offset: the importer resolves the clinic's own timezone. */
    occurredAt: IsoDateTimeSchema,
    timeKnown: z.boolean().optional(),
    durationMinutes: z.number().int().min(0).max(1440).optional(),
    sourceStatus: z.string().max(40).optional(),
    status: z.enum(["completed", "cancelled", "no_show", "booked", "unknown"]),
    noteSignedInSource: z.boolean().optional(),
    provider: z
        .object({
        display: z.string().max(160).optional(),
        billingId: z.string().max(40).optional(),
        kind: z.enum(["physician", "staff", "online_booking", "unknown"]).optional(),
    })
        .strict()
        .optional(),
    reason: z.string().max(500).optional(),
    staffNotes: z.string().max(4000).optional(),
    noteType: z.string().max(80).optional(),
    matchKind: z.enum(["exact", "date_only", "none"]).optional(),
    note: z
        .object({
        sourceReference: z.string().max(240).optional(),
        text: z.string().max(100_000),
        sections: z
            .object({
            history: z.string().optional(),
            physicalExam: z.string().optional(),
            assessmentPlan: z.string().optional(),
            medicationsPrescribed: z.string().optional(),
            vitals: z.string().optional(),
        })
            .strict()
            .optional(),
        authorDisplay: z.string().max(160).optional(),
        authorRole: z.string().max(40).optional(),
        authoredAt: IsoDateTimeSchema.optional(),
        reviewerDisplay: z.string().max(160).optional(),
        reviewerBillingId: z.string().max(40).optional(),
        reviewedAt: IsoDateTimeSchema.optional(),
    })
        .strict()
        .optional(),
})
    .strict();
const sourcePatientKey = z.string().min(1).max(160);
export const ImportPatientRequestSchema = z.discriminatedUnion("section", [
    z
        .object({
        section: z.literal("demographics"),
        sourcePatientKey,
        registration: RegisterPatientInputSchema,
        /** Required by the `link` policy: the operator names the match. */
        linkExistingPatientId: IdSchema.optional(),
    })
        .strict(),
    z
        .object({
        section: z.literal("identifiers"),
        sourcePatientKey,
        identifiers: z.array(ImportPatientExternalIdentifierSchema).max(20),
    })
        .strict(),
    z
        .object({
        section: z.literal("profile"),
        sourcePatientKey,
        allergies: z.array(ImportAllergySchema).max(200).optional(),
        medications: z.array(ImportMedicationSchema).max(200).optional(),
        pmh: z.array(ImportPmhSchema).max(200).optional(),
        familyHistory: z
            .object({ rows: z.array(ImportFamilyHistoryRowSchema).max(200) })
            .strict()
            .optional(),
        socialHistory: z.array(ImportSocialHistorySchema).max(200).optional(),
        immunizations: z.array(ImportImmunizationSchema).max(200).optional(),
    })
        .strict(),
    z
        .object({
        section: z.literal("labs"),
        sourcePatientKey,
        labName: z.string().min(1).max(160),
        accessions: z.array(ImportLabAccessionSchema).max(50),
    })
        .strict(),
    z
        .object({
        section: z.literal("visits"),
        sourcePatientKey,
        visits: z.array(ImportLegacyVisitSchema).max(200),
    })
        .strict(),
    z
        .object({
        section: z.literal("administrative_notes"),
        sourcePatientKey,
        narrative: z.string().min(1).max(4000),
        entries: z.array(z.record(z.string(), z.unknown())).optional(),
    })
        .strict(),
    z
        .object({
        section: z.literal("complete"),
        sourcePatientKey,
        status: z.enum(["active", "inactive", "deceased"]).optional(),
        warmContext: z.boolean().optional(),
    })
        .strict(),
]);
export const ImportPatientReceiptSchema = z
    .object({
    runId: IdSchema,
    sourcePatientKey,
    patientId: IdSchema.optional(),
    section: z.string().min(1).max(40),
    counts: z
        .object({
        created: z.number().int().nonnegative(),
        linked: z.number().int().nonnegative(),
        unchanged: z.number().int().nonnegative(),
        skipped: z.number().int().nonnegative(),
        conflict: z.number().int().nonnegative(),
    })
        .passthrough(),
    items: z
        .array(z
        .object({
        itemKey: z.string().min(1).max(240),
        status: ImportItemStatusSchema,
        targetId: IdSchema.optional(),
        detail: ImportItemDetailSchema.optional(),
    })
        .passthrough())
        .optional(),
})
    .passthrough();
export const UpdatePatientStatusRequestSchema = z
    .object({
    siteId: IdSchema,
    status: z.enum(["active", "inactive", "deceased"]),
    expectedVersion: z.number().int().positive(),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const UpdatePatientStatusReceiptSchema = z
    .object({
    updated: z.boolean(),
    patientId: IdSchema,
    status: z.enum(["active", "inactive", "deceased"]),
    version: z.number().int().positive(),
})
    .passthrough();
//# sourceMappingURL=patient-import.js.map